import React, { useState, useEffect } from 'react';
import { FaEdit, FaBolt, FaUser, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Dashboard = () => {
    const [devices, setDevices] = useState([]);
    const [userIdToUsername, setUserIdToUsername] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const loggedUser = JSON.parse(localStorage.getItem('user'));

    useEffect(() => {
        const fetchDevicesAndUsers = async () => {
            try {
                setLoading(true);
                // Pobieramy urządzenia
                const devicesResponse = await api.get('/devices/');
                setDevices(devicesResponse.data);

                // Pobieramy użytkowników
                const usersResponse = await api.get('/users/');
                const users = usersResponse.data;

                // Tworzymy mapę user_id => username
                const userMap = {};
                users.forEach((user) => {
                    userMap[user.id] = user.username;
                });
                setUserIdToUsername(userMap);
                setError(null);
            } catch (error) {
                console.error('Failed to fetch devices or users', error);
                setError('Błąd podczas pobierania danych');
            } finally {
                setLoading(false);
            }
        };

        fetchDevicesAndUsers();

        // Odświeżamy dane co 30 sekund
        const interval = setInterval(() => {
            fetchDevicesAndUsers();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const goToDataEntry = (deviceId) => {
        navigate(`/data-entry?device_id=${deviceId}`);
    };
    
    // Renderowanie tabeli na urządzeniach mobilnych
    const renderMobileDeviceList = () => {
        return devices.map((device) => (
            <div key={device.id} className="mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                <div className="flex items-center mb-3">
                    <FaBolt className="text-softTeal mr-2" size={18} />
                    <span className="font-bold text-lg">{device.name}</span>
                </div>
                <div className="mb-2 flex items-center">
                    <FaUser className="text-softTeal mr-2" size={16} />
                    <span>{userIdToUsername[device.user_id] || '...'}</span>
                </div>
                <div className="mb-3 flex items-center">
                    <FaCalendarAlt className="text-softTeal mr-2" size={16} />
                    <span>{device.activation_date}</span>
                </div>
                {loggedUser.role === 'admin' && (
                    <button
                        className="w-full bg-softTeal text-pureWhite px-4 py-3 rounded-lg hover:bg-warmOrange transition flex items-center justify-center"
                        onClick={() => goToDataEntry(device.id)}
                    >
                        <FaEdit className="mr-2" /> Wprowadzanie danych
                    </button>
                )}
            </div>
        ));
    };

    // Renderowanie tradycyjnej tabeli na większe ekrany
    const renderDesktopTable = () => {
        return (
            <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="border px-4 py-2 text-left">Nazwa</th>
                        <th className="border px-4 py-2 text-left">Nazwa użytkownika</th>
                        <th className="border px-4 py-2 text-left">Data aktywacji</th>
                        {loggedUser.role === 'admin' && (
                            <th className="border px-4 py-2 text-left">Akcje</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {devices.map((device) => (
                        <tr key={device.id}>
                            <td className="border px-4 py-2">
                                <div className="flex items-center">
                                    <FaBolt className="text-softTeal mr-2" />
                                    {device.name}
                                </div>
                            </td>
                            <td className="border px-4 py-2">
                                <div className="flex items-center">
                                    <FaUser className="text-softTeal mr-2" />
                                    {userIdToUsername[device.user_id] || '...'}
                                </div>
                            </td>
                            <td className="border px-4 py-2">
                                <div className="flex items-center">
                                    <FaCalendarAlt className="text-softTeal mr-2" />
                                    {device.activation_date}
                                </div>
                            </td>
                            {loggedUser.role === 'admin' && (
                                <td className="border px-4 py-2">
                                    <button
                                        className="bg-softTeal text-pureWhite px-4 py-2 rounded-lg hover:bg-warmOrange transition flex items-center"
                                        onClick={() => goToDataEntry(device.id)}
                                    >
                                        <FaEdit className="mr-2" /> Wprowadzanie danych
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className="min-h-screen flex flex-col items-center p-4 sm:p-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-4xl">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                    PPE
                </h2>
                
                {loading ? (
                    <p className="text-center py-4">Ładowanie danych...</p>
                ) : error ? (
                    <p className="text-red-500 text-center py-4">{error}</p>
                ) : devices.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nie znaleziono urządzeń.</p>
                ) : (
                    <>
                        <div className="md:hidden">
                            {renderMobileDeviceList()}
                        </div>
                        
                        <div className="hidden md:block overflow-x-auto">
                            {renderDesktopTable()}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
